import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

/**
 * BottomTabMenu - Componente de menú flotante inferior reutilizable
 *
 * @param {Object} props
 * @param {string} props.baseRoute - Ruta base para navegar (ej: '/clientes/123')
 * @param {Array} props.tabs - Array de objetos con configuración de cada tab
 * @param {string} props.currentTab - Tab activa actual
 */
const BottomTabMenu = ({ baseRoute, tabs, currentTab }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Tabs por defecto para módulo de clientes (labels más cortos)
  const defaultTabs = [
    {
      id: 'datos',
      label: 'Datos',
      route: ''
    },
    {
      id: 'creditos',
      label: 'Créditos',
      route: '/creditos'
    },
    {
      id: 'historial',
      label: 'Historial de Compra',
      route: '/historial'
    },
    {
      id: 'estado-cuenta',
      label: 'Estado Cuenta',
      route: '/estado-cuenta'
    },
    {
      id: 'pagos',
      label: 'Pagos Hechos',
      route: '/pagos'
    },
    {
      id: 'pagar',
      label: 'Pagar Facturas',
      route: '/pagar'
    },
    {
      id: 'notas',
      label: 'Notas',
      route: '/notas'
    },
    {
      id: 'mensajes',
      label: 'Enviar Mensajes',
      route: '/mensajes'
    }
  ];

  const menuTabs = tabs || defaultTabs;

  const handleTabClick = (tab) => {
    // Si la ruta empieza con '/', es absoluta; sino, se concatena con baseRoute
    const fullRoute = tab.route.startsWith('/system') ? tab.route : `${baseRoute}${tab.route}`;

    // Si la ruta completa es igual a la ruta actual, no hacer nada
    if (location.pathname === fullRoute) {
      return;
    }

    navigate(fullRoute);
  };

  const isActiveTab = (tabId) => {
    return currentTab === tabId || location.pathname.includes(tabId);
  };

  // Verificar si necesita scroll y actualizar las flechas
  const checkScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  // Scroll suave hacia la izquierda o derecha
  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  // Verificar scroll al montar y al cambiar tamaño
  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [menuTabs]);

  // Verificar scroll al hacer scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
      return () => container.removeEventListener('scroll', checkScroll);
    }
  }, []);

  return (
    <div className="bottom-tab-menu">
      {showLeftArrow && (
        <button
          className="scroll-arrow scroll-arrow-left"
          onClick={() => scroll('left')}
          aria-label="Scroll left"
        >
          <FiChevronLeft size={20} />
        </button>
      )}

      <div className="tab-menu-container" ref={scrollContainerRef}>
        {menuTabs.map((tab) => {
          const isActive = isActiveTab(tab.id);

          return (
            <button
              key={tab.id}
              className={`tab-menu-item ${isActive ? 'active' : ''}`}
              onClick={() => handleTabClick(tab)}
            >
              <span className="tab-label">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {showRightArrow && (
        <button
          className="scroll-arrow scroll-arrow-right"
          onClick={() => scroll('right')}
          aria-label="Scroll right"
        >
          <FiChevronRight size={20} />
        </button>
      )}
    </div>
  );
};

export default BottomTabMenu;
